// src/pages/properties/PropertyEditPage.tsx
import {useState, useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Save, X, MapPin, Upload, ArrowLeft, Image} from "lucide-react";
import {FormField, PageHeader} from "../../components/common";
import {Button, Card, Badge} from "../../components/ui";
import {useToast} from "../../hooks/common/useToast";
import {useProperty, useProperties} from "../../hooks/useProperties";
import {useRegions} from "../../hooks/useRegions";
import {usePropertyTypes} from "../../hooks/usePropertyTypes";
import {ROUTES} from "../../utils/constants";
import {formatCurrency} from "../../utils/formatters";
import type {UpdatePropertyDto} from "../../types/property";
import {useTownshipsByRegion} from "../../hooks/useTownship";
import PropertyPhotoManager from "./components/PropertyPhotoManager";

const PropertyEditPage: React.FC = () => {
  const navigate = useNavigate();
  const {id} = useParams<{id: string}>();
  const {success, error} = useToast();

  const propertyId = id ? parseInt(id) : undefined;

  // Form state
  const [formData, setFormData] = useState<Partial<UpdatePropertyDto>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Data hooks
  const {property, isLoading: propertyLoading} = useProperty(propertyId);
  const {handleUpdate} = useProperties();
  const {dropdownRegions, isLoading: regionsLoading} = useRegions();
  const {townships, isLoading: townshipsLoading} = useTownshipsByRegion(
    formData.regionId || undefined
  );
  const {propertyTypes, isLoading: typesLoading} = usePropertyTypes();

  // Initialize form data when property loads
  // PropertyEditPage.tsx - Fixed validation issues

  // ✅ Enhanced form validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required string fields with minimum length
    if (!formData.titleEng?.trim() || formData.titleEng.trim().length < 5) {
      newErrors.titleEng = "English title must be at least 5 characters";
    }
    if (!formData.titleMm?.trim() || formData.titleMm.trim().length < 5) {
      newErrors.titleMm = "Myanmar title must be at least 5 characters";
    }

    // ✅ Price validation - allow 0 for initialization but require valid price on submit
    if (
      formData.price === undefined ||
      formData.price === null ||
      formData.price <= 1000
    ) {
      newErrors.price = "Price must be at least 1,000 MMK";
    }

    // ✅ Required select fields - check for valid IDs (not 0 or undefined)
    if (!formData.regionId || formData.regionId === 0) {
      newErrors.regionId = "Region is required";
    }
    if (!formData.townshipId || formData.townshipId === 0) {
      newErrors.townshipId = "Township is required";
    }
    if (!formData.propertyTypeId || formData.propertyTypeId === 0) {
      newErrors.propertyTypeId = "Property type is required";
    }

    // ✅ Required enum fields
    if (!formData.listingType || formData.listingType === "") {
      newErrors.listingType = "Listing type is required";
    }
    if (!formData.developmentType || formData.developmentType === "") {
      newErrors.developmentType = "Development type is required";
    }
    if (!formData.finishStatus || formData.finishStatus === "") {
      newErrors.finishStatus = "Finish status is required";
    }

    // Description validation (for rich text editor)
    const getPlainText = (html: string) => {
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || div.innerText || "";
    };

    if (
      !formData.descriptionEng ||
      getPlainText(formData.descriptionEng).trim().length < 10
    ) {
      newErrors.descriptionEng =
        "English description must be at least 10 characters";
    }
    if (
      !formData.descriptionMm ||
      getPlainText(formData.descriptionMm).trim().length < 10
    ) {
      newErrors.descriptionMm =
        "Myanmar description must be at least 10 characters";
    }

    // ✅ Number validations - only validate if values are provided
    if (
      formData.bedrooms !== undefined &&
      formData.bedrooms !== null &&
      (formData.bedrooms < 0 || formData.bedrooms > 20)
    ) {
      newErrors.bedrooms = "Bedrooms must be between 0 and 20";
    }
    if (
      formData.bathrooms !== undefined &&
      formData.bathrooms !== null &&
      (formData.bathrooms < 0 || formData.bathrooms > 20)
    ) {
      newErrors.bathrooms = "Bathrooms must be between 0 and 20";
    }

    // ✅ Coordinate validation - only if provided
    if (
      formData.latitude !== undefined &&
      formData.latitude !== null &&
      (formData.latitude < -90 || formData.latitude > 90)
    ) {
      newErrors.latitude = "Latitude must be between -90 and 90";
    }
    if (
      formData.longitude !== undefined &&
      formData.longitude !== null &&
      (formData.longitude < -180 || formData.longitude > 180)
    ) {
      newErrors.longitude = "Longitude must be between -180 and 180";
    }
    console.log("newErrors", newErrors);

    setErrors(newErrors);

    // ✅ Debug logging to help identify validation issues
    if (Object.keys(newErrors).length > 0) {
      console.log("Validation errors:", newErrors);
      console.log("Current form data:", formData);
    }

    return Object.keys(newErrors).length === 0;
  };

  // ✅ Enhanced form data initialization
  useEffect(() => {
    if (property) {
      const data: Partial<UpdatePropertyDto> = {
        titleEng: property.titleEng || "",
        titleMm: property.titleMm || "",
        price: property.price || 0,
        listingType: property.listingType || "",
        developmentType: property.developmentType || "",
        finishStatus: property.finishStatus || "",
        bankStatus: property.bankStatus || false,
        sizeWidth: property.sizeWidth || "",
        sizeHeight: property.sizeHeight || "",
        sizeArea: property.sizeArea || "",
        bedrooms: property.bedrooms || 0,
        bathrooms: property.bathrooms || 0,
        descriptionEng: property.descriptionEng || "",
        descriptionMm: property.descriptionMm || "",
        mapCoordinates: property.mapCoordinates || "",
        // ✅ Ensure proper ID assignment
        regionId: property.region?.id || 0,
        townshipId: property.township?.id || 0,
        propertyTypeId: property.propertyType?.id || 0,
        status: property.status || "active",
      };

      // ✅ Handle coordinates properly
      if (property.latitude !== null && property.latitude !== undefined) {
        data.latitude = property.latitude;
      }
      if (property.longitude !== null && property.longitude !== undefined) {
        data.longitude = property.longitude;
      }

      setFormData(data);
    }
  }, [property]);

  // Handle form field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({...prev, [field]: value}));

    // Clear error when field is modified
    if (errors[field]) {
      setErrors((prev) => ({...prev, [field]: ""}));
    }

    // ✅ Reset township when region changes - set to 0 instead of null
    if (field === "regionId") {
      setFormData((prev) => ({...prev, townshipId: 0}));
    }
  };

  // ✅ Clean data before sending to API
  const cleanFormData = (
    data: Partial<UpdatePropertyDto>
  ): UpdatePropertyDto => {
    const cleaned: any = {};

    // Handle string fields - convert empty strings to undefined/null as needed
    [
      "titleEng",
      "titleMm",
      "descriptionEng",
      "descriptionMm",
      "sizeWidth",
      "sizeHeight",
      "sizeArea",
      "mapCoordinates",
    ].forEach((field) => {
      const value = data[field as keyof UpdatePropertyDto];
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.length > 0) {
          cleaned[field] = trimmed;
        }
      }
    });

    // ✅ Handle required number fields
    if (data.price !== undefined && data.price !== null && data.price > 0) {
      cleaned.price = Number(data.price);
    }

    if (
      data.regionId !== undefined &&
      data.regionId !== null &&
      data.regionId > 0
    ) {
      cleaned.regionId = Number(data.regionId);
    }

    if (
      data.townshipId !== undefined &&
      data.townshipId !== null &&
      data.townshipId > 0
    ) {
      cleaned.townshipId = Number(data.townshipId);
    }

    if (
      data.propertyTypeId !== undefined &&
      data.propertyTypeId !== null &&
      data.propertyTypeId > 0
    ) {
      cleaned.propertyTypeId = Number(data.propertyTypeId);
    }

    // ✅ Handle optional number fields
    ["bedrooms", "bathrooms", "latitude", "longitude"].forEach((field) => {
      const value = data[field as keyof UpdatePropertyDto];
      if (value !== undefined && value !== null && value !== "") {
        const numValue = Number(value);
        if (!isNaN(numValue)) {
          cleaned[field] = numValue;
        }
      }
    });

    // ✅ Handle required enum fields
    ["listingType", "developmentType", "finishStatus"].forEach((field) => {
      const value = data[field as keyof UpdatePropertyDto];
      if (value && value !== "") {
        cleaned[field] = value;
      }
    });

    // ✅ Handle optional fields
    if (data.status && data.status !== "") {
      cleaned.status = data.status;
    }

    if (data.bankStatus !== undefined) {
      cleaned.bankStatus = Boolean(data.bankStatus);
    }

    return cleaned;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!propertyId) {
      error("Error", "Property ID not found");
      return;
    }

    if (!validateForm()) {
      error("Validation Error", "Please fix the errors and try again");
      return;
    }

    setIsSubmitting(true);

    try {
      // ✅ Clean the data before sending
      const cleanedData = cleanFormData(formData);

      console.log("Sending cleaned data:", cleanedData); // Debug log

      const result = await handleUpdate(propertyId, cleanedData);

      if (result) {
        success("Success", "Property updated successfully");
        navigate(ROUTES.PROPERTIES);
      }
    } catch (err: any) {
      console.error("Update error:", err); // Debug log

      // ✅ Better error handling
      let errorMessage = "Failed to update property";
      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      error("Error", errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(ROUTES.PROPERTIES);
  };

  if (propertyLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Property not found
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          The property you're looking for doesn't exist or has been deleted.
        </p>
        <Button onClick={() => navigate(ROUTES.PROPERTIES)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Properties
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title={`Edit Property - ${property.uniqueId}`}
        breadcrumbs={[
          {label: "Dashboard", path: "/"},
          {label: "Properties", path: ROUTES.PROPERTIES},
          {label: "Edit"},
        ]}
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Basic Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  name="titleEng"
                  label="Title (English)"
                  type="text"
                  value={formData.titleEng || ""}
                  onChange={(value) => handleFieldChange("titleEng", value)}
                  error={errors.titleEng || ""}
                  required
                  placeholder="Enter property title in English (min 5 chars)"
                />

                <FormField
                  name="titleMm"
                  label="Title (Myanmar)"
                  type="text"
                  value={formData.titleMm || ""}
                  onChange={(value) => handleFieldChange("titleMm", value)}
                  error={errors.titleMm || ""}
                  required
                  placeholder="Enter property title in Myanmar (min 5 chars)"
                />

                <FormField
                  name="price"
                  label="Price (MMK)"
                  type="number"
                  value={formData.price ?? 0}
                  onChange={(value) =>
                    handleFieldChange("price", Number(value) || undefined)
                  }
                  error={errors.price || ""}
                  required
                  min={1000}
                  placeholder="Enter price in Myanmar Kyats (min 1,000)"
                />

                <FormField
                  name="listingType"
                  label="Listing Type"
                  type="select"
                  value={formData.listingType || ""}
                  onChange={(value) => handleFieldChange("listingType", value)}
                  required
                  options={[
                    {value: "", label: "Select Listing Type", disabled: true},
                    {value: "sale", label: "For Sale"},
                    {value: "rent", label: "For Rent"},
                  ]}
                />

                <FormField
                  name="developmentType"
                  label="Development Type"
                  type="select"
                  value={formData.developmentType || ""}
                  onChange={(value) =>
                    handleFieldChange("developmentType", value)
                  }
                  required
                  options={[
                    {
                      value: "",
                      label: "Select Development Type",
                      disabled: true,
                    },
                    {value: "normal", label: "Normal Development"},
                    {value: "new", label: "New Development"},
                  ]}
                />

                <FormField
                  name="finishStatus"
                  label="Finish Status"
                  type="select"
                  value={formData.finishStatus || ""}
                  onChange={(value) => handleFieldChange("finishStatus", value)}
                  required
                  options={[
                    {value: "", label: "Select Finish Status", disabled: true},
                    {value: "not_finished", label: "Not Finished"},
                    {value: "partly_finished", label: "Partly Finished"},
                    {value: "totally_finished", label: "Totally Finished"},
                  ]}
                />

                <FormField
                  name="bedrooms"
                  label="Bedrooms"
                  type="number"
                  value={formData.bedrooms || 0}
                  onChange={(value) =>
                    handleFieldChange("bedrooms", Number(value) || 0)
                  }
                  error={errors.bedrooms || ""}
                  min={0}
                  max={20}
                />

                <FormField
                  name="bathrooms"
                  label="Bathrooms"
                  type="number"
                  value={formData.bathrooms || 0}
                  onChange={(value) =>
                    handleFieldChange("bathrooms", Number(value) || 0)
                  }
                  error={errors.bathrooms || ""}
                  min={0}
                  max={20}
                />
              </div>
            </Card>

            {/* Location Information */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Location Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  name="regionId"
                  label="Region"
                  type="select"
                  value={formData.regionId || ""}
                  onChange={(value) =>
                    handleFieldChange("regionId", Number(value) || undefined)
                  }
                  error={errors.regionId || ""}
                  required
                  disabled={regionsLoading}
                  options={[
                    {value: "", label: "Select Region", disabled: true},
                    ...dropdownRegions.map((region) => ({
                      value: region.id,
                      label: region.nameEng,
                    })),
                  ]}
                />

                <FormField
                  name="townshipId"
                  label="Township"
                  type="select"
                  value={formData.townshipId || ""}
                  onChange={(value) =>
                    handleFieldChange("townshipId", Number(value) || undefined)
                  }
                  error={errors.townshipId || ""}
                  required
                  disabled={!formData.regionId || townshipsLoading}
                  options={[
                    {value: "", label: "Select Township", disabled: true},
                    ...townships.map((township) => ({
                      value: township.id,
                      label: township.nameEng,
                    })),
                  ]}
                />

                <FormField
                  name="propertyTypeId"
                  label="Property Type"
                  type="select"
                  value={formData.propertyTypeId || ""}
                  onChange={(value) =>
                    handleFieldChange(
                      "propertyTypeId",
                      Number(value) || undefined
                    )
                  }
                  error={errors.propertyTypeId || ""}
                  required
                  disabled={typesLoading}
                  options={[
                    {value: "", label: "Select Type", disabled: true},
                    ...propertyTypes.map((type) => ({
                      value: type.id,
                      label: `${type.icon || ""} ${type.nameEng}`.trim(),
                    })),
                  ]}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <FormField
                  name="latitude"
                  label="Latitude"
                  type="number"
                  value={formData.latitude || 0}
                  onChange={(value) => {
                    const numValue = Number(value);
                    handleFieldChange(
                      "latitude",
                      isNaN(numValue) ? undefined : numValue
                    );
                  }}
                  error={errors.latitude || ""}
                  placeholder="16.7967129"
                />

                <FormField
                  name="longitude"
                  label="Longitude"
                  type="number"
                  value={formData.longitude || 0}
                  onChange={(value) => {
                    const numValue = Number(value);
                    handleFieldChange(
                      "longitude",
                      isNaN(numValue) ? undefined : numValue
                    );
                  }}
                  error={errors.longitude || ""}
                  placeholder="96.1609916"
                />

                <FormField
                  name="mapCoordinates"
                  label="Map Coordinates"
                  type="text"
                  value={formData.mapCoordinates || ""}
                  onChange={(value) =>
                    handleFieldChange("mapCoordinates", value)
                  }
                  placeholder="16.7967129, 96.1609916"
                />
              </div>
            </Card>

            {/* Size Information */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Size Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  name="sizeWidth"
                  label="Width"
                  type="text"
                  value={formData.sizeWidth || ""}
                  onChange={(value) => handleFieldChange("sizeWidth", value)}
                  placeholder="40ft"
                />

                <FormField
                  name="sizeHeight"
                  label="Height"
                  type="text"
                  value={formData.sizeHeight || ""}
                  onChange={(value) => handleFieldChange("sizeHeight", value)}
                  placeholder="60ft"
                />

                <FormField
                  name="sizeArea"
                  label="Area"
                  type="text"
                  value={formData.sizeArea || ""}
                  onChange={(value) => handleFieldChange("sizeArea", value)}
                  placeholder="2400 sq ft"
                />
              </div>
            </Card>

            {/* Description */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Description
              </h3>

              <div className="space-y-4">
                <FormField
                  name="descriptionEng"
                  label="Description (English)"
                  type="richtext"
                  value={formData.descriptionEng || ""}
                  onChange={(value) =>
                    handleFieldChange("descriptionEng", value)
                  }
                  error={errors.descriptionEng || ""}
                  required
                  placeholder="Enter detailed description in English (min 10 chars)"
                />

                <FormField
                  name="descriptionMm"
                  label="Description (Myanmar)"
                  type="richtext"
                  value={formData.descriptionMm || ""}
                  onChange={(value) =>
                    handleFieldChange("descriptionMm", value)
                  }
                  error={errors.descriptionMm || ""}
                  required
                  placeholder="Enter detailed description in Myanmar (min 10 chars)"
                />
              </div>
            </Card>

            {/* Photo Management */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <Image className="w-5 h-5 mr-2" />
                Property Photos
              </h3>

              <PropertyPhotoManager property={property} />
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Property Status */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Property Status
              </h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Property ID:</span>
                  <span className="font-mono text-sm text-primary-600 dark:text-primary-400">
                    {property.uniqueId}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Current Status:</span>
                  <Badge
                    variant={
                      property.status === "active"
                        ? "success"
                        : property.status === "inactive"
                        ? "danger"
                        : "gray"
                    }
                  >
                    {property.status}
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Views:</span>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {property.reads}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Photos:</span>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {property.photoCount}
                  </span>
                </div>
              </div>
            </Card>

            {/* Status & Settings */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Settings
              </h3>

              <div className="space-y-4">
                <FormField
                  name="status"
                  label="Status"
                  type="select"
                  value={formData.status || ""}
                  onChange={(value) => handleFieldChange("status", value)}
                  options={[
                    {value: "", label: "Select Status", disabled: true},
                    {value: "active", label: "Active"},
                    {value: "inactive", label: "Inactive"},
                    {value: "sold", label: "Sold"},
                    {value: "rented", label: "Rented"},
                  ]}
                />

                <FormField
                  name="bankStatus"
                  label="Bank Financing Available"
                  type="checkbox"
                  checked={formData.bankStatus || false}
                  onChange={(checked) =>
                    handleFieldChange("bankStatus", checked)
                  }
                  checkboxLabel="Yes, bank financing is available"
                />
              </div>
            </Card>

            {/* Form Actions */}
            <Card className="p-6">
              <div className="space-y-3">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSubmitting ? "Updating..." : "Update Property"}
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </Card>

            {/* Quick Preview */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Quick Preview
              </h3>

              <div className="space-y-3">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {formData.titleEng || property.titleEng}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formData.titleMm || property.titleMm}
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Price:</span>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(formData.price || property.price)}
                    </p>
                    <Badge
                      variant={
                        (formData.listingType || property.listingType) ===
                        "sale"
                          ? "success"
                          : "info"
                      }
                      size="sm"
                    >
                      {(formData.listingType || property.listingType) === "sale"
                        ? "For Sale"
                        : "For Rent"}
                    </Badge>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Rooms:</span>
                  <span className="text-gray-900 dark:text-white">
                    {formData.bedrooms || property.bedrooms} bed,{" "}
                    {formData.bathrooms || property.bathrooms} bath
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PropertyEditPage;
